/**
 * @file src/shell/ToolRail.js
 * CỘT CÔNG CỤ — khay nổi ở góc TRÊN-PHẢI sân khấu, chứa nút công cụ của shell. Mỗi
 * nút CHỈ có icon (không còn chữ dưới icon — xem lịch sử review "gọn"), viền tròn
 * 44×44. Có một đường KẺ MẢNH tách nút "Cài đặt" khỏi bốn nút còn lại — Cài đặt là
 * hành động khác nhóm (mở panel cấu hình), không phải một "công cụ xem" như 4 nút kia.
 *
 * Trước đây file này là BottomBar.js (dải ngang ở đáy). Đáy giờ là thanh bước của
 * tiết (StepBar.js) nên công cụ dời sang cột dọc bên phải. Tên file theo VAI, không
 * theo vị trí — xem chú thích đầu StepBar.js.
 *
 * Nhãn i18n (labelKey) KHÔNG còn hiện trên mặt nút, nhưng vẫn dùng cho `title`/
 * `aria-label` — nút icon-only vẫn phải có tên đọc được cho trình đọc màn hình.
 */

import { icon } from './icons.js';

/** id của shell — tiết đăng ký trùng sẽ bị đổi thành `<id>-2`. Xem registry.js */
export const SHELL_BUTTON_IDS = ['rotate', 'labels', 'sound', 'info', 'settings'];

/**
 * @param {Object} o
 * @param {HTMLElement} o.root
 * @param {(k: string, v?: any) => string} o.t
 * @param {import('./registry.js').Registry<any>} o.buttons
 * @param {import('./registry.js').Registry<any>} o.sliders
 */
export function createToolRail(o) {
  const el = document.createElement('aside');
  el.className = 'ol-toolrail';
  o.root.appendChild(el);

  function render() {
    el.innerHTML = '';

    const group = document.createElement('div');
    group.className = 'ol-toolgroup';

    // Slider của tiết (nếu có) đứng trước nhóm nút, cùng một khối.
    for (const slot of o.sliders.list()) {
      const d = slot.data;
      const wrap = document.createElement('div');
      wrap.className = 'ol-slider';
      wrap.innerHTML = `<span class="ol-icon">${icon(d.icon ?? 'zoom')}</span>`;
      const input = document.createElement('input');
      input.type = 'range';
      input.className = 'ol-range';
      input.min = String(d.min);
      input.max = String(d.max);
      input.step = String(d.step ?? 1);
      input.value = String(d.value);
      input.title = o.t(d.labelKey);
      input.setAttribute('aria-label', o.t(d.labelKey));
      input.addEventListener('input', () => d.onChange?.(Number(input.value)));
      wrap.appendChild(input);
      group.appendChild(wrap);
    }

    for (const slot of o.buttons.list()) {
      const d = slot.data;
      if (d.hidden) continue;

      // "Cài đặt" tách khỏi 4 nút xem bằng một đường kẻ mảnh — nó mở panel cấu
      // hình, khác nhóm với Xoay/Nhãn/Âm thanh/Thông tin (đều là công cụ XEM).
      if (slot.id === 'settings') {
        const sep = document.createElement('span');
        sep.className = 'ol-tool__sep';
        group.appendChild(sep);
      }

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'ol-tool';
      btn.dataset.id = slot.id;
      const text = o.t(d.labelKey);
      btn.title = text;
      btn.setAttribute('aria-label', text);
      if (d.toggle) {
        btn.classList.toggle('is-active', Boolean(d.state));
        btn.setAttribute('aria-pressed', String(Boolean(d.state)));
      }
      if (d.disabled) btn.disabled = true;

      // Nút icon-only — không còn <span class="ol-tool__label"> hiện chữ dưới icon
      // (xem chú thích đầu file). title/aria-label ở trên đã lo phần đọc được.
      btn.innerHTML = `<span class="ol-tool__icon"><span class="ol-icon">${icon(d.icon)}</span></span>`;

      btn.addEventListener('click', () => d.onClick?.(d.toggle ? !d.state : undefined));
      group.appendChild(btn);
    }

    el.classList.toggle('is-empty', group.children.length === 0);
    if (group.children.length) el.appendChild(group);
  }

  const offB = o.buttons.onChange(render);
  const offS = o.sliders.onChange(render);
  render();

  return {
    el,
    render,
    /** @param {boolean} on */
    setCompact(on) {
      el.classList.toggle('is-compact', on);
    },
    dispose() {
      offB();
      offS();
      el.remove();
    },
  };
}
